import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ProfileCompleteness } from '@/components/ProfileCompleteness';
import { ProfileHealth } from '@/components/ProfileHealth';
import BillingModal from '@/components/BillingModal';
import {
  getFunderProfile,
  createFunderProfile,
  getFunderLeads,
  respondToLead,
  analyzeCustomer,
  getFunderAnalyses,
  getCreditMemo,
  getNotifications,
  getUnreadCount,
  getCountries,
  getSectors,
  getFunderTypes,
  exportCreditMemoPDF,
  exportCreditMemoWord,
} from '@/lib/api';
import {
  Landmark,
  Users,
  FileText,
  Bell,
  LogOut,
  Brain,
  CheckCircle,
  Clock,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Menu,
  X,
  User,
  Target,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Download,
  Plus,
  FileDown,
  CreditCard,
  FileUp,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const NAV_ITEMS = [
  { id: 'dashboard', icon: Landmark, label: 'Dashboard' },
  { id: 'leads', icon: Target, label: 'Leads' },
  { id: 'analyses', icon: Brain, label: 'Customer Analysis' },
  { id: 'profile', icon: User, label: 'Profile' },
  { id: 'billing', icon: CreditCard, label: 'Billing' },
];

const LEAD_STATUS_CONFIG = {
  NEW: { color: 'bg-blue-100 text-blue-700', icon: Clock },
  VIEWED: { color: 'bg-slate-100 text-slate-700', icon: Eye },
  INTERESTED: { color: 'bg-emerald-100 text-emerald-700', icon: ThumbsUp },
  PASSED: { color: 'bg-red-100 text-red-700', icon: ThumbsDown },
  WON: { color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
};

function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function FunderDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [funderTypes, setFunderTypes] = useState([]);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);

  const [profileForm, setProfileForm] = useState({
    organization_name: '',
    funder_type: '',
    country: '',
    regions_served: [],
    sectors_focus: [],
    min_ticket_size: '',
    max_ticket_size: '',
    preferred_tenors: [],
    currency_preferences: [],
    risk_appetite: 'MODERATE',
    description: '',
    contact_email: '',
    contact_phone: '',
  });

  const [analysisForm, setAnalysisForm] = useState({
    customer_name: '',
    sector: '',
    country: '',
    loan_amount: '',
    currency: 'USD',
    tenor_months: '12',
    purpose: '',
    annual_revenue: '',
    years_in_business: '',
    collateral_value: '',
    existing_debt: '',
    fx_exposure: '',
  });

  const [analysisDocuments, setAnalysisDocuments] = useState([]);
  const [uploadingAnalysisDoc, setUploadingAnalysisDoc] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [countriesData, sectorsData, funderTypesData] = await Promise.all([
        getCountries(),
        getSectors(),
        getFunderTypes(),
      ]);
      setCountries(countriesData);
      setSectors(sectorsData);
      setFunderTypes(funderTypesData);

      try {
        const profileData = await getFunderProfile();
        setProfile(profileData);
      } catch (e) {
        if (e.response?.status === 404) {
          // Redirect to onboarding wizard
          navigate('/funder/onboarding');
          return;
        }
      }

      const [leadsData, analysesData, notifsData, unreadData] = await Promise.all([
        getFunderLeads().catch(() => []),
        getFunderAnalyses().catch(() => []),
        getNotifications(),
        getUnreadCount(),
      ]);
      setLeads(leadsData);
      setAnalyses(analysesData);
      setNotifications(notifsData);
      setUnreadCount(unreadData.unread_count);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...profileForm,
        min_ticket_size: profileForm.min_ticket_size ? parseFloat(profileForm.min_ticket_size) : null,
        max_ticket_size: profileForm.max_ticket_size ? parseFloat(profileForm.max_ticket_size) : null,
        preferred_tenors: profileForm.preferred_tenors.length > 0 ? profileForm.preferred_tenors.map(Number) : [],
      };
      
      const newProfile = await createFunderProfile(data);
      setProfile(newProfile);
      setShowProfileForm(false);
      toast.success('Funder profile created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnalyzeCustomer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(analysisForm).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      // Add documents to form data
      analysisDocuments.forEach((doc, index) => {
        if (doc.file) {
          formData.append('documents', doc.file);
        }
      });

      const docCount = analysisDocuments.length;
      toast.info(`Running AI analysis${docCount > 0 ? ` with ${docCount} document(s)...` : '...'}`);
      const result = await analyzeCustomer(formData);
      setAnalyses([result, ...analyses]);
      setShowAnalysisForm(false);
      setAnalysisDocuments([]);
      setAnalysisForm({
        customer_name: '',
        sector: '',
        country: '',
        loan_amount: '',
        currency: 'USD',
        tenor_months: '12',
        purpose: '',
        annual_revenue: '',
        years_in_business: '',
        collateral_value: '',
        existing_debt: '',
        fx_exposure: '',
      });
      
      const docsReviewed = result.documents_reviewed || 0;
      toast.success(`Analysis complete! Risk Grade: ${result.risk_grade}, Decision: ${result.decision}${docsReviewed > 0 ? ` (${docsReviewed} docs reviewed)` : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespondToLead = async (leadId, interested) => {
    try {
      await respondToLead(leadId, interested);
      await fetchData();
      toast.success(interested ? 'Marked as interested!' : 'Lead passed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const newLeads = leads.filter(l => l.status === 'NEW').length;
  const interestedLeads = leads.filter(l => l.status === 'INTERESTED').length;
  const totalPipeline = leads.filter(l => ['NEW', 'VIEWED', 'INTERESTED'].includes(l.status))
    .reduce((sum, l) => sum + l.loan_amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show profile form if no profile exists
  if (showProfileForm || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Apaka" className="h-12" />
          </div>
          
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Create Your Funder Profile</CardTitle>
              <CardDescription>Tell us about your organization to receive matched opportunities</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateProfile} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={profileForm.organization_name}
                      onChange={(e) => setProfileForm({ ...profileForm, organization_name: e.target.value })}
                      required
                      placeholder="ABC Development Finance"
                      data-testid="profile-org-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Funder Type *</Label>
                    <Select
                      value={profileForm.funder_type}
                      onValueChange={(v) => setProfileForm({ ...profileForm, funder_type: v })}
                      required
                    >
                      <SelectTrigger data-testid="profile-funder-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {funderTypes.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country *</Label>
                    <Select
                      value={profileForm.country}
                      onValueChange={(v) => setProfileForm({ ...profileForm, country: v })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Risk Appetite</Label>
                    <Select
                      value={profileForm.risk_appetite}
                      onValueChange={(v) => setProfileForm({ ...profileForm, risk_appetite: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CONSERVATIVE">Conservative</SelectItem>
                        <SelectItem value="MODERATE">Moderate</SelectItem>
                        <SelectItem value="AGGRESSIVE">Aggressive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Ticket Size (USD)</Label>
                    <Input
                      type="number"
                      value={profileForm.min_ticket_size}
                      onChange={(e) => setProfileForm({ ...profileForm, min_ticket_size: e.target.value })}
                      placeholder="100000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Ticket Size (USD)</Label>
                    <Input
                      type="number"
                      value={profileForm.max_ticket_size}
                      onChange={(e) => setProfileForm({ ...profileForm, max_ticket_size: e.target.value })}
                      placeholder="10000000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    placeholder="Brief description of your organization and investment focus..."
                    rows={3}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={profileForm.contact_email}
                      onChange={(e) => setProfileForm({ ...profileForm, contact_email: e.target.value })}
                      placeholder="deals@organization.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={profileForm.contact_phone}
                      onChange={(e) => setProfileForm({ ...profileForm, contact_phone: e.target.value })}
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-profile-btn">
                  {submitting ? 'Creating...' : 'Create Profile'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 h-16 flex items-center justify-between px-4">
        <img src={LOGO_URL} alt="Apaka" className="h-10 brightness-0 invert" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-white">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-slate-900 text-white z-40 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-4 border-b border-slate-800">
          <img src={LOGO_URL} alt="Apaka" className="h-12 brightness-0 invert" />
        </div>
        
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center">
              <Landmark className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{profile?.organization_name}</div>
              <div className="text-xs text-slate-400">{profile?.funder_type?.replace('_', ' ')}</div>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            item.link ? (
              <Link
                key={item.id}
                to={item.link}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ) : (
              <button
                key={item.id}
                onClick={() => { 
                  if (item.id === 'billing') {
                    setShowBillingModal(true);
                  } else {
                    setActiveTab(item.id); 
                  }
                  setSidebarOpen(false); 
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
                {item.id === 'leads' && newLeads > 0 && (
                  <span className="ml-auto bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {newLeads}
                  </span>
                )}
              </button>
            )
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="page-title">
                {activeTab === 'dashboard' && 'Funder Dashboard'}
                {activeTab === 'leads' && 'Investment Leads'}
                {activeTab === 'analyses' && 'Customer Analysis'}
                {activeTab === 'profile' && 'Organization Profile'}
              </h1>
              <p className="text-slate-500">Welcome back, {user?.full_name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Button onClick={fetchData} variant="outline" size="icon">
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Profile Completeness & Health */}
              <div className="grid md:grid-cols-2 gap-4">
                <ProfileCompleteness profile={profile} type="funder" />
                <ProfileHealth userRole="FUNDER" />
              </div>
              
              {/* Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-slate-200" data-testid="stat-new-leads">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Target className="h-4 w-4" />
                      <span className="text-sm">New Leads</span>
                    </div>
                    <div className="text-2xl font-bold">{newLeads}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-interested">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <ThumbsUp className="h-4 w-4" />
                      <span className="text-sm">Interested</span>
                    </div>
                    <div className="text-2xl font-bold">{interestedLeads}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-pipeline">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-sm">Pipeline Value</span>
                    </div>
                    <div className="text-2xl font-bold">{formatCurrency(totalPipeline)}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-analyses">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Brain className="h-4 w-4" />
                      <span className="text-sm">Analyses</span>
                    </div>
                    <div className="text-2xl font-bold">{analyses.length}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-4">
                  <Button onClick={() => setActiveTab('leads')} className="bg-purple-600 hover:bg-purple-700" data-testid="view-leads-btn">
                    <Target className="h-4 w-4 mr-2" />
                    View Leads ({newLeads} new)
                  </Button>
                  <Button variant="outline" onClick={() => { setActiveTab('analyses'); setShowAnalysisForm(true); }}>
                    <Brain className="h-4 w-4 mr-2" />
                    Analyze Customer
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Leads */}
              {leads.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading">Recent Leads</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('leads')}>
                      View All
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {leads.slice(0, 3).map((lead) => {
                        const StatusIcon = LEAD_STATUS_CONFIG[lead.status]?.icon || Clock;
                        return (
                          <div key={lead.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                            <div>
                              <div className="font-medium">{lead.company_name}</div>
                              <div className="text-sm text-slate-500">
                                {formatCurrency(lead.loan_amount, lead.currency)} • {lead.sector} • {lead.country}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline">{lead.match_score}% match</Badge>
                              <Badge className={LEAD_STATUS_CONFIG[lead.status]?.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {lead.status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notifications */}
              {notifications.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading">Recent Notifications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {notifications.slice(0, 5).map((notif) => (
                        <div key={notif.id} className={`p-4 rounded-lg ${notif.is_read ? 'bg-slate-50' : 'bg-purple-50 border border-purple-100'}`}>
                          <div className="font-medium">{notif.title}</div>
                          <div className="text-sm text-slate-500">{notif.message}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div className="space-y-6">
              {leads.length === 0 ? (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Target className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-xl font-medium mb-2">No Leads Yet</h3>
                    <p className="text-slate-500">When companies submit applications matching your criteria, they'll appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {leads.map((lead) => {
                    const StatusIcon = LEAD_STATUS_CONFIG[lead.status]?.icon || Clock;
                    return (
                      <Card key={lead.id} className="border-slate-200">
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-xl font-bold">{lead.company_name}</span>
                                <Badge className={LEAD_STATUS_CONFIG[lead.status]?.color}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {lead.status}
                                </Badge>
                                {lead.risk_grade && (
                                  <Badge variant="outline">{lead.risk_grade}</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                <span>{formatCurrency(lead.loan_amount, lead.currency)}</span>
                                <span>{lead.tenor_months} months</span>
                                <span>{lead.sector}</span>
                                <span>{lead.country}</span>
                                <span className="text-purple-600 font-medium">{lead.match_score}% match</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {lead.status === 'NEW' && (
                                <>
                                  <Button
                                    variant="outline"
                                    onClick={() => handleRespondToLead(lead.id, false)}
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    data-testid={`pass-${lead.id}`}
                                  >
                                    <ThumbsDown className="h-4 w-4 mr-2" />
                                    Pass
                                  </Button>
                                  <Button
                                    onClick={() => handleRespondToLead(lead.id, true)}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid={`interested-${lead.id}`}
                                  >
                                    <ThumbsUp className="h-4 w-4 mr-2" />
                                    Interested
                                  </Button>
                                </>
                              )}
                              {lead.status !== 'NEW' && (
                                <Button variant="outline">
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Analyses Tab */}
          {activeTab === 'analyses' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => setShowAnalysisForm(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="new-analysis-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Analyze Customer
                </Button>
              </div>

              {showAnalysisForm && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle>Analyze Customer</CardTitle>
                    <CardDescription>Enter customer data for AI-powered credit analysis</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAnalyzeCustomer} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Customer Name *</Label>
                          <Input
                            value={analysisForm.customer_name}
                            onChange={(e) => setAnalysisForm({ ...analysisForm, customer_name: e.target.value })}
                            required
                            placeholder="Acme Corp"
                            data-testid="analysis-customer-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sector *</Label>
                          <Select
                            value={analysisForm.sector}
                            onValueChange={(v) => setAnalysisForm({ ...analysisForm, sector: v })}
                            required
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select sector" />
                            </SelectTrigger>
                            <SelectContent>
                              {sectors.map((s) => (
                                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Country *</Label>
                          <Select
                            value={analysisForm.country}
                            onValueChange={(v) => setAnalysisForm({ ...analysisForm, country: v })}
                            required
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((c) => (
                                <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Loan Amount *</Label>
                          <Input
                            type="number"
                            value={analysisForm.loan_amount}
                            onChange={(e) => setAnalysisForm({ ...analysisForm, loan_amount: e.target.value })}
                            required
                            placeholder="1000000"
                            data-testid="analysis-loan-amount"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <Select value={analysisForm.currency} onValueChange={(v) => setAnalysisForm({ ...analysisForm, currency: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="GBP">GBP</SelectItem>
                              <SelectItem value="NGN">NGN</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tenor (months)</Label>
                          <Select value={analysisForm.tenor_months} onValueChange={(v) => setAnalysisForm({ ...analysisForm, tenor_months: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="6">6 months</SelectItem>
                              <SelectItem value="12">12 months</SelectItem>
                              <SelectItem value="24">24 months</SelectItem>
                              <SelectItem value="36">36 months</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Purpose *</Label>
                          <Input
                            value={analysisForm.purpose}
                            onChange={(e) => setAnalysisForm({ ...analysisForm, purpose: e.target.value })}
                            required
                            placeholder="Working capital"
                            data-testid="analysis-purpose"
                          />
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h4 className="font-medium mb-4">Financial Information</h4>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Annual Revenue (USD)</Label>
                            <Input
                              type="number"
                              value={analysisForm.annual_revenue}
                              onChange={(e) => setAnalysisForm({ ...analysisForm, annual_revenue: e.target.value })}
                              placeholder="5000000"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Years in Business</Label>
                            <Input
                              type="number"
                              value={analysisForm.years_in_business}
                              onChange={(e) => setAnalysisForm({ ...analysisForm, years_in_business: e.target.value })}
                              placeholder="5"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Collateral Value (USD)</Label>
                            <Input
                              type="number"
                              value={analysisForm.collateral_value}
                              onChange={(e) => setAnalysisForm({ ...analysisForm, collateral_value: e.target.value })}
                              placeholder="500000"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Document Upload Section */}
                      <div className="border-t pt-4 mt-4">
                        <h4 className="font-medium mb-4 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Supporting Documents (Optional)
                        </h4>
                        <p className="text-sm text-slate-500 mb-4">
                          Upload financial statements, contracts, and other documents for more accurate analysis.
                        </p>
                        
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                          <input 
                            type="file" 
                            id="analysis-doc-upload" 
                            className="hidden" 
                            multiple
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length === 0) return;
                              
                              const newDocs = files.map(file => ({
                                file,
                                filename: file.name,
                                document_type: file.name.toLowerCase().includes('financial') ? 'financial_statement' : 
                                              file.name.toLowerCase().includes('contract') ? 'contract' : 
                                              file.name.toLowerCase().includes('id') ? 'id_document' : 'other',
                                file_size: file.size,
                              }));
                              
                              setAnalysisDocuments(prev => [...prev, ...newDocs]);
                              e.target.value = '';
                            }}
                          />
                          <FileUp className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600 mb-2">
                            Drag and drop files here, or
                          </p>
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={() => document.getElementById('analysis-doc-upload')?.click()}
                          >
                            Choose Files
                          </Button>
                          <p className="text-xs text-slate-400 mt-2">
                            PDF, Word, Excel, Images (max 10MB each)
                          </p>
                        </div>

                        {analysisDocuments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-slate-700">Documents to analyze ({analysisDocuments.length}):</p>
                            {analysisDocuments.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-purple-600" />
                                  <div>
                                    <p className="text-sm font-medium">{doc.filename}</p>
                                    <p className="text-xs text-slate-500">{doc.document_type} • {(doc.file_size / 1024).toFixed(1)} KB</p>
                                  </div>
                                </div>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setAnalysisDocuments(prev => prev.filter((_, i) => i !== idx))}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3 justify-end mt-4">
                        <Button type="button" variant="outline" onClick={() => { setShowAnalysisForm(false); setAnalysisDocuments([]); }}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} className="bg-purple-600 hover:bg-purple-700" data-testid="run-analysis-btn">
                          {submitting ? (
                            <>
                              <Brain className="h-4 w-4 mr-2 animate-pulse" />
                              Analyzing{analysisDocuments.length > 0 ? ` ${analysisDocuments.length} docs...` : '...'}
                            </>
                          ) : (
                            <>
                              <Brain className="h-4 w-4 mr-2" />
                              Run Analysis{analysisDocuments.length > 0 ? ` (${analysisDocuments.length} docs)` : ''}
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Analyses List */}
              <div className="space-y-4">
                {analyses.map((analysis) => (
                  <Card key={analysis.id} className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xl font-bold">{analysis.customer_name}</span>
                            <Badge className={analysis.decision === 'CONDITIONAL_APPROVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {analysis.decision}
                            </Badge>
                            {analysis.risk_grade && (
                              <Badge variant="outline">{analysis.risk_grade}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                            <span>{formatCurrency(analysis.loan_amount, analysis.currency)}</span>
                            <span>{analysis.tenor_months} months</span>
                            <span>{analysis.sector}</span>
                            <span>{analysis.country}</span>
                          </div>
                          
                          {analysis.risk_factors && (
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Credit Risk:</span>
                                <span className="ml-2 font-medium">{analysis.risk_factors.credit_risk}/100</span>
                              </div>
                              <div>
                                <span className="text-slate-500">FX Risk:</span>
                                <span className="ml-2 font-medium">{analysis.risk_factors.fx_risk}/100</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Sovereign:</span>
                                <span className="ml-2 font-medium">{analysis.risk_factors.sovereign_risk}/100</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Climate:</span>
                                <span className="ml-2 font-medium">{analysis.risk_factors.climate_risk}/100</span>
                              </div>
                            </div>
                          )}

                          {analysis.key_risks?.length > 0 && (
                            <div className="mt-4">
                              <span className="text-sm text-slate-500">Key Risks: </span>
                              <span className="text-sm">{analysis.key_risks.join(', ')}</span>
                            </div>
                          )}

                          {/* Document Assessment */}
                          {analysis.documents_reviewed > 0 && (
                            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="h-4 w-4 text-purple-600" />
                                <span className="text-sm font-medium text-purple-900">
                                  {analysis.documents_reviewed} Document(s) Reviewed
                                </span>
                              </div>
                              {analysis.document_assessment && (
                                <div className="text-sm text-purple-700">
                                  {analysis.document_assessment.completeness_score !== undefined && (
                                    <span className="mr-4">Completeness: {analysis.document_assessment.completeness_score}%</span>
                                  )}
                                  {analysis.document_assessment.missing_documents?.length > 0 && (
                                    <span className="text-amber-600">
                                      Missing: {analysis.document_assessment.missing_documents.join(', ')}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={async () => {
                              try {
                                const blob = await exportCreditMemoPDF(analysis.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `credit_memo_${analysis.customer_name?.replace(/\s+/g, '_') || 'analysis'}.pdf`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('PDF downloaded successfully');
                              } catch (error) {
                                toast.error('Failed to download PDF');
                              }
                            }}
                          >
                            <FileDown className="h-4 w-4 mr-2" />
                            PDF
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={async () => {
                              try {
                                const blob = await exportCreditMemoWord(analysis.id);
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `credit_memo_${analysis.customer_name?.replace(/\s+/g, '_') || 'analysis'}.docx`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('Word document downloaded successfully');
                              } catch (error) {
                                toast.error('Failed to download Word document');
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Word
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {analyses.length === 0 && !showAnalysisForm && (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Brain className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-xl font-medium mb-2">No Analyses Yet</h3>
                    <p className="text-slate-500 mb-4">Analyze your customers to get AI-powered credit insights</p>
                    <Button onClick={() => setShowAnalysisForm(true)} className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Analyze Customer
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && profile && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Organization Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-slate-500">Organization Name</Label>
                      <p className="font-medium">{profile.organization_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Funder Type</Label>
                      <p className="font-medium">{profile.funder_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Country</Label>
                      <p className="font-medium">{profile.country}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Risk Appetite</Label>
                      <p className="font-medium">{profile.risk_appetite}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Ticket Size Range</Label>
                      <p className="font-medium">
                        {profile.min_ticket_size ? formatCurrency(profile.min_ticket_size) : 'N/A'} - {profile.max_ticket_size ? formatCurrency(profile.max_ticket_size) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Contact Email</Label>
                      <p className="font-medium">{profile.contact_email || '-'}</p>
                    </div>
                  </div>
                  {profile.description && (
                    <div className="mt-6">
                      <Label className="text-slate-500">Description</Label>
                      <p className="mt-1">{profile.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Billing Modal */}
      <BillingModal open={showBillingModal} onClose={() => setShowBillingModal(false)} />
    </div>
  );
}
